import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { Subscription } from 'rxjs';
import { HeroReportService } from '../../model/hero-report/hero-report.service';
import { HeroReport } from '../../model/hero-report/hero-report.model';
import { HeroReportImageService } from '../../model/hero-report-image/hero-report-image.service';

import { StepperHeaderComponent } from '../../shared/stepper-header/stepper-header.component';
import { HeroReportStep1Component } from './hero-report-step1/hero-report-step1.component';
import { HeroReportStep2Component } from './hero-report-step2/hero-report-step2.component';
import { HeroReportStep3Component } from './hero-report-step3/hero-report-step3.component';

import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { AbstractControl, ValidationErrors } from '@angular/forms';

import { ErrorMessageComponent } from '../../shared/error-message/error-message.component';

import { v4 as uuidv4 } from 'uuid';
import getDataUrlImageType from '../../utilities/getDataUrlImageType';
import { mgrsValidator } from '../../utilities/location';
import { MAX_MGRS_LENGTH } from '../../utilities/sreReport';
import { normalizeMgrs } from '../../utilities/mgrs';

function atLeastOneOverallImageValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (
    control &&
    Array.isArray(control.value) &&
    control.value.length > 0
  ) {
    return null;
  }
  return { noOverallImage: true };
}

@Component({
  selector: 'app-hero-report-stepper',
  standalone: true,
  imports: [
    StepperHeaderComponent,
    HeroReportStep1Component,
    HeroReportStep2Component,
    HeroReportStep3Component,
    ButtonModule,
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ErrorMessageComponent,
  ],
  templateUrl: './hero-report-stepper.component.html',
  styleUrls: ['./hero-report-stepper.component.scss'],
})
export class HeroReportStepperComponent implements OnInit, OnDestroy {
  step = 0;
  isSubmitting = false;

  itemForm!: FormGroup;
  eventForm!: FormGroup;
  evacForm!: FormGroup;

  stepValid = [false, false, false];
  private subs: Subscription[] = [];

  steps = [
    { label: $localize`:@@stepper.item:Item` },
    { label: $localize`:@@stepper.event:Event` },
    { label: $localize`:@@stepper.evacuation:Evacuation` }
  ];
  
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private heroReportService: HeroReportService,
    private heroReportImageService: HeroReportImageService
  ) { }

  ngOnInit() {
    // Get query params for lat/lng
    const lat = this.route.snapshot.queryParamMap.get('lat');
    const lng = this.route.snapshot.queryParamMap.get('lng');
    if (!lat || !lng) {
      // If missing, go back to events
      this.router.navigate(['/events']);
      return;
    }
    this.buildForms();
    this.hookValidation();
    // Optionally patch lat/lng into the appropriate form
    if (this.itemForm) {
      this.itemForm.patchValue({
        latitude: parseFloat(lat),
        longitude: parseFloat(lng),
      });
    }
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }

  private buildForms() {
    this.itemForm = this.fb.group(
      {
        possibleIdentification: ['', Validators.required],
        evacuationPerimeterM: [null],
        latitude: [null, Validators.required],
        longitude: [null, Validators.required],
        mgrs: ['', [Validators.required, Validators.maxLength(MAX_MGRS_LENGTH), mgrsValidator]],
        overallImages: this.fb.array([], atLeastOneOverallImageValidator),
        noseImages: this.fb.array([]),
        tailImages: this.fb.array([]),
      }
    );

    this.eventForm = this.fb.group({
      firstNoticedDate: null,
      pocName: [''],
      pocPhone: [''],
      previousFindings: [null],
      wasItemMoved: [null],
      liquidOrCrystallization: [null],
      deadAnimalsOrFoliage: [null],
      casualties: [''],
    });

    // mirror your HeroReportComponent numberValidator()
    const numberPattern = Validators.pattern(/^-?\d+(\.\d+)?$/);

    this.evacForm = this.fb.group({
      evacuationPerimeterM: ['', numberPattern],
      infrastructurePresent: [null],
      radioTransmitters: [null],
      powerLines: [null],
      fuelTanks: [null],
      buildingsInFragZone: [null],
      additionalHazards: [''],
    });

    // conditional validators for event date
    // FIXME
    /*
    this.subs.push(
      this.eventForm.get('unknown')!.valueChanges.subscribe((unknown) => {
        if (unknown) {
          valCtl.clearValidators();
          unitCtl.clearValidators();
        } else {
          valCtl.setValidators([Validators.required, numberPattern]);
          unitCtl.setValidators([Validators.required]);
        }
        valCtl.updateValueAndValidity({ emitEvent: false });
        unitCtl.updateValueAndValidity({ emitEvent: false });
      })
    );
     */
  }

  private hookValidation() {
    this.subs.push(
      this.itemForm.statusChanges.subscribe((status) => {
        this.stepValid[0] = status === 'VALID';
      })
    );
    this.subs.push(
      this.eventForm.statusChanges.subscribe((status) => {
        this.stepValid[1] = status === 'VALID';
      })
    );
    this.subs.push(
      this.evacForm.statusChanges.subscribe((status) => {
        this.stepValid[2] = status === 'VALID';
      })
    );
  }

  nextStep() {
    const forms = [this.itemForm, this.eventForm, this.evacForm];
    const currentForm = forms[this.step]!;

    currentForm.markAllAsTouched();

    currentForm.updateValueAndValidity();

    this.stepValid[this.step] = currentForm.valid;

    if (this.step === 0 && currentForm.valid) {
      this.evacForm.patchValue(this.itemForm.value);
    }

    if (currentForm.valid) {
      this.step++;
    }
  }

  prevStep() {
    if (this.step > 0) {
      this.step--;
    }
  }

  async submit() {
    if (!this.stepValid.every(Boolean)) return;

    this.isSubmitting = true;
    const combined: HeroReport = {
      ...this.itemForm.value,
      ...this.eventForm.value,
      ...this.evacForm.value,
    };

    // Gather all images from the three arrays
    const allImages = [
      ...(this.itemForm.get('overallImages') as FormArray).value,
      ...(this.itemForm.get('noseImages') as FormArray).value,
      ...(this.itemForm.get('tailImages') as FormArray).value,
    ];

    // The MGRS is saved cleaned up.
    combined.mgrs = normalizeMgrs(combined.mgrs);

    try {
      combined.id = uuidv4();
      combined.status = 'PENDING';

      const heroReport = await this.heroReportService.create(combined);
      const heroReportId = heroReport.id;

      // Upload all images that are data URLs
      for (const img of allImages) {
        if (img.url && img.url.startsWith('data:')) {
          await this.heroReportImageService.createAndUpload({
            url: img.url,
            type: img.tag,
            fileType: getDataUrlImageType(img.url),
            heroReportId,
            displayOrder: allImages.indexOf(img),
            id: uuidv4(),
          });
        }
      }

      this.router.navigate(['/events']);
    } finally {
      this.isSubmitting = false;
    }
  }

  get allStepsValid(): boolean {
    return this.stepValid.every((v) => v);
  }

  goBack() {
    this.router.navigate(['/events'], { queryParams: { recenter: 'true' } });
  }
}
